import { useCallback, useMemo, lazy } from "react";
import { useTranslation } from "react-i18next";
import { X, Download, RefreshCw } from "lucide-react";
import { Button } from "@stockline/ui/components/button";
import { ExcelViewer } from "@/components/pdf-viewer/ExcelViewer";
import type { BoundingBox, TableLine, TableRow } from "@/components/pdf-viewer";
import { ZoomControls } from "./ZoomControls";
import type { ImageBand } from "@/app/recap/types/delineation.types";
import {
  strongShapesForFlows,
  type SourceRegions,
} from "@/app/recap/services/sourceRegions";
import { useSourceHoverIds } from "@/app/recap/stores/sourceHoverStore";
import type { SourceRegion } from "@/components/pdf-viewer/regionGeometry";
import { ImageViewer } from "./ImageViewer";
import { HtmlPreviewWithHighlight } from "./HtmlPreviewWithHighlight";
import { TextPreviewWithHighlight } from "./TextPreviewWithHighlight";

// Stable empty fallbacks for every `resting`/`sourceRegions` prop below. A
// fresh `[]` literal per render would defeat the very effects these props
// feed: HtmlPreviewWithHighlight's hover-listener effect and its
// `applyHtmlRowHighlight` paint effect are both keyed on `restingRows`'
// identity, ExcelViewer rebuilds a `Set` from `restingRows` every render, and
// TextPreviewWithHighlight does the same for `restingTextLines` — none of
// that should re-run just because the PARENT re-rendered.
const NO_ROWS: number[] = [];
const NO_TABLE_ROWS: TableRow[] = [];
const NO_BANDS: ImageBand[] = [];
const NO_REGIONS: SourceRegion[] = [];

/** Reverse hover-highlight wiring (STK-2338, doc → flow): the document's
 *  regions plus the single handler every viewer's hover callback funnels
 *  into. Absent ⇒ the panel behaves exactly as before — the /chat
 *  (`EmailMessageDisplay`) and /ai (`SourceDocumentPanelBody`) callers pass
 *  nothing and stay out of scope. */
export interface SourceHoverWiring {
  regions: SourceRegions;
  onHover: (flowIds: string[] | null) => void;
}

const CustomPDFViewer = lazy(() =>
  import("@/components/pdf-viewer").then((mod) => ({
    default: mod.CustomPDFViewer,
  })),
);

/** `report.xlsx` → `report-lignes.xlsx` for the per-order slice download. */
function deriveSliceFilename(attachmentName: string): string {
  const dot = attachmentName.lastIndexOf(".");
  const base = dot > 0 ? attachmentName.slice(0, dot) : attachmentName;
  return `${base}-lignes.xlsx`;
}

/** The shapes each surface should paint: the reverse direction's strong layer
 *  (the pointer is inside one of THIS document's regions) when there is one,
 *  else the forward direction's flow-hover props.
 *
 *  The two can never disagree in practice — the pointer cannot be over a flow
 *  row and inside the document preview at once — but precedence is explicit
 *  rather than assumed. */
function resolveEffectiveShapes(
  strong: ReturnType<typeof strongShapesForFlows> | null,
  forward: {
    tableRows: TableRow[];
    imageBands: ImageBand[];
    excelRows: number[];
    htmlRows: number[];
    textLines: number[];
  },
) {
  if (!strong) return forward;
  return {
    tableRows: strong.tableRows,
    imageBands: strong.imageBands,
    excelRows: strong.ordinals,
    htmlRows: strong.ordinals,
    textLines: strong.ordinals,
  };
}

/** Download + close, for the states that render no viewer toolbar. The panel
 *  header that used to host them is gone, so without these the user is trapped
 *  in a loading / error / unsupported preview with no way out. */
function FallbackControls({
  onClose,
  onDownload,
  isDownloading,
}: Readonly<{
  onClose: () => void;
  onDownload: () => void;
  isDownloading: boolean;
}>) {
  const { t } = useTranslation();
  const downloadLabel = t(isDownloading ? "chat.downloading" : "chat.download");
  return (
    <div className="absolute top-2 right-2 flex items-center gap-1">
      <Button
        variant="ghost"
        size="sm"
        onClick={onDownload}
        disabled={isDownloading}
        className="h-8 w-8 p-0"
        title={downloadLabel}
        aria-label={downloadLabel}
      >
        <Download className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={onClose}
        className="h-8 w-8 p-0"
        aria-label={t("common.close", { defaultValue: "Close" })}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}

export function PreviewContent({
  previewLoading,
  previewError,
  fileType,
  documentPdfData,
  documentArrayBuffer,
  documentBlobUrl,
  htmlContent,
  textContent,
  boundingBoxes,
  tableLines,
  tableRows,
  highlightedExcelRows,
  highlightedRows,
  highlightedTextLines,
  imageBands,
  focusedRowId,
  attachmentName,
  onClose,
  onDownload,
  isDownloading,
  onRetry,
  scale,
  rotation,
  onScaleChange,
  onRotationChange,
  excelExtraData,
  excelExtraDataSourceRowColumn,
  sourceHover,
  showRestingBands,
}: Readonly<{
  previewLoading: boolean;
  previewError: string | null;
  fileType: string;
  documentPdfData: Uint8Array | null;
  documentArrayBuffer: ArrayBuffer | null;
  documentBlobUrl: string | null;
  htmlContent: string | null;
  textContent: string | null;
  boundingBoxes: BoundingBox[];
  tableLines: TableLine[];
  tableRows: TableRow[];
  highlightedExcelRows: number[];
  /** Hovered flow's html element ordinals (delineation.rows). */
  highlightedRows: number[];
  /** Hovered flow's text line numbers (delineation.rows). */
  highlightedTextLines: number[];
  imageBands: ImageBand[];
  focusedRowId: string | null;
  attachmentName: string;
  onClose: () => void;
  onDownload: () => void;
  isDownloading: boolean;
  onRetry?: () => void;
  scale: number;
  rotation: number;
  onScaleChange: (s: number) => void;
  onRotationChange: (r: number) => void;
  excelExtraData?: ArrayBuffer | Uint8Array;
  /** 0-based column in `excelExtraData` holding each slice row's row number
   *  in the original workbook — see ExcelViewer's `extraDataSourceRowColumn`. */
  excelExtraDataSourceRowColumn?: number;
  /** STK-2338 reverse hover: absent ⇒ no resting layer, no hover callbacks. */
  sourceHover?: SourceHoverWiring;
  /** Pointer-inside gate — the viewers paint their resting prop unconditionally
   *  whenever it's non-empty, so revealing on enter / hiding on leave lives
   *  entirely here. */
  showRestingBands: boolean;
}>) {
  const { t } = useTranslation();
  const regions = sourceHover?.regions;
  const resting = showRestingBands ? regions : undefined;

  // Reverse hover (doc → flow, STK-2338): while the pointer rests inside a
  // region of THIS document, its flow(s) strengthen here regardless of the
  // forward-direction (flow → doc) props below.
  const hoveredIds = useSourceHoverIds();
  const strong = useMemo(
    () =>
      regions && hoveredIds.length > 0
        ? strongShapesForFlows(regions, hoveredIds)
        : null,
    [regions, hoveredIds],
  );
  const effective = resolveEffectiveShapes(strong, {
    tableRows,
    imageBands,
    excelRows: highlightedExcelRows,
    htmlRows: highlightedRows,
    textLines: highlightedTextLines,
  });

  // Shared by the three ordinal-speaking surfaces (excel/html/text). Wrapped in
  // useCallback so its identity only changes when `sourceHover` does —
  // HtmlPreviewWithHighlight rebinds its DOM listeners in an effect keyed on
  // this callback's identity, so an unstable function would rebind on every
  // render, not just on genuine hover-source changes.
  const hoverOrdinal = useCallback(
    (ordinal: number | null) => {
      if (!sourceHover) return;
      const ids =
        ordinal == null ? null : regions?.flowIdsByOrdinal.get(ordinal);
      sourceHover.onHover(ids?.length ? ids : null);
    },
    [sourceHover, regions],
  );
  // Hoisted once: `sourceHover ? hoverOrdinal : undefined` was repeated at each
  // ordinal-speaking surface below.
  const onOrdinalHover = sourceHover ? hoverOrdinal : undefined;
  const fallbackControls = (
    <FallbackControls
      onClose={onClose}
      onDownload={onDownload}
      isDownloading={isDownloading}
    />
  );
  if (previewLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        {fallbackControls}
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">{t("chat.loadingPreview")}</p>
        </div>
      </div>
    );
  }
  if (previewError) {
    return (
      <div className="h-full flex items-center justify-center">
        {fallbackControls}
        <div className="text-center">
          <p className="text-destructive">{t("chat.failedToLoadDocument")}</p>
          {onRetry && (
            <Button
              size="sm"
              variant="outline"
              onClick={onRetry}
              className="mt-3"
            >
              <RefreshCw className="h-4 w-4 mr-1.5" />
              {t("ui.retry")}
            </Button>
          )}
        </div>
      </div>
    );
  }
  if (fileType === "pdf" && documentPdfData) {
    return (
      <CustomPDFViewer
        fileData={documentPdfData}
        boundingBoxes={boundingBoxes}
        tableLines={tableLines}
        tableRows={effective.tableRows}
        focusedRowId={focusedRowId}
        onBoundingBoxClick={() => {}}
        onTableRowClick={() => {}}
        className="h-full"
        initialScale={scale}
        initialRotation={rotation}
        onScaleChange={onScaleChange}
        onRotationChange={onRotationChange}
        onClose={onClose}
        onDownload={onDownload}
        isDownloading={isDownloading}
        sourceRegions={regions?.pdfRegions ?? NO_REGIONS}
        restingRows={resting?.tableRows ?? NO_TABLE_ROWS}
        onSourceHover={sourceHover?.onHover}
      />
    );
  }
  if (fileType === "excel" && documentArrayBuffer) {
    // The per-order source-rows "View" (STK-1269) is a SECOND Excel buffer — a
    // slice of just this order's rows — handed to the same ExcelViewer. The
    // toolbar toggle flips between the file and the slice through one render
    // path, so the slice looks identical to the file and every tool keeps
    // working. When no order context supplies rows, `excelExtraData` is
    // undefined → no toggle, sheet only.
    return (
      <ExcelViewer
        data={documentArrayBuffer}
        filename={attachmentName}
        className="h-full"
        onClose={onClose}
        extraData={excelExtraData}
        extraDataLabel={t("ordersRecap.sourceRowsShowOnly")}
        extraDataShowAllLabel={t("ordersRecap.sourceRowsShowAll")}
        extraFilename={deriveSliceFilename(attachmentName)}
        extraDataSourceRowColumn={excelExtraDataSourceRowColumn}
        highlightedRows={effective.excelRows}
        restingRows={resting?.excelRows ?? NO_ROWS}
        onRowHover={onOrdinalHover}
      />
    );
  }
  if (fileType === "image" && documentBlobUrl) {
    return (
      <div className="flex flex-col h-full">
        <ZoomControls
          scale={scale}
          onScaleChange={onScaleChange}
          onReset={() => {
            onScaleChange(1);
            onRotationChange(0);
          }}
          rotation={rotation}
          onRotationChange={onRotationChange}
          onClose={onClose}
          onDownload={onDownload}
          isDownloading={isDownloading}
        />
        <ImageViewer
          src={documentBlobUrl}
          alt={attachmentName}
          scale={scale}
          rotation={rotation}
          imageBands={effective.imageBands}
          sourceRegions={regions?.photoRegions ?? NO_REGIONS}
          restingBands={resting?.imageBands ?? NO_BANDS}
          onSourceHover={sourceHover?.onHover}
        />
      </div>
    );
  }
  if (fileType === "html" && htmlContent) {
    return (
      <HtmlPreviewWithHighlight
        htmlContent={htmlContent}
        attachmentName={attachmentName}
        highlightedRows={effective.htmlRows}
        scale={scale}
        onScaleChange={onScaleChange}
        onClose={onClose}
        onDownload={onDownload}
        isDownloading={isDownloading}
        restingRows={resting?.excelRows ?? NO_ROWS}
        onRowHover={onOrdinalHover}
      />
    );
  }
  if (fileType === "text" && textContent !== null) {
    return (
      <TextPreviewWithHighlight
        textContent={textContent}
        highlightedTextLines={effective.textLines}
        scale={scale}
        onScaleChange={onScaleChange}
        onClose={onClose}
        onDownload={onDownload}
        isDownloading={isDownloading}
        restingTextLines={resting?.excelRows ?? NO_ROWS}
        onLineHover={onOrdinalHover}
      />
    );
  }
  return (
    <div className="h-full flex items-center justify-center">
      {fallbackControls}
      <p className="text-muted-foreground">{t("chat.previewNotAvailable")}</p>
    </div>
  );
}
